import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromApp from './store/app.reducer';
import { logout } from './auth/store/auth.actions';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private messageService = inject(MessageService);
  private store = inject(Store<fromApp.AppState>);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Skip reporting errors during auto-login attempt if they are 401/403 or DRF pagination errors
        // that often happen because of unauthenticated empty querysets
        const authState = this.store.selectSignal((state) => state.auth)();
        const isAuthError = error.status === 401 || error.status === 403;
        const isInvalidPageError = error.status === 404 && error.error?.detail === 'Invalid page.';

        if (!authState.autoLoginAttempted && (isAuthError || isInvalidPageError)) {
          return throwError(() => error);
        }

        if (error.status === 401 && authState.user) {
          this.store.dispatch(logout());
          return throwError(() => error);
        }

        // Additional safeguard: if we have a user but are still getting Auth errors,
        // it might be a transient state during logout or token expiration,
        // but "Invalid page" specifically is usually a result of queryset filtering by user.
        if (isInvalidPageError && !authState.user) {
           return throwError(() => error);
        }

        const errorMessage = this.formatErrorMessage(error);

        this.messageService.add({
          severity: 'error',
          summary: this.getErrorSummary(error.status),
          detail: errorMessage,
          life: 7000,
        });

        return throwError(() => error);
      })
    );
  }

  private formatErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;

    // 400 Bad Request: format DRF validation errors
    if (error.status === 400 && body && typeof body === 'object') {
      return this.formatValidationErrors(body);
    }

    // 500: generic server error
    if (error.status === 500) {
      return 'A apărut o eroare pe server. Încearcă din nou mai târziu.';
    }

    // 403: forbidden
    if (error.status === 403) {
      if (body?.detail) return body.detail;
      return 'Nu ai permisiunea să efectuezi această acțiune.';
    }

    // 404: not found
    if (error.status === 404) {
      if (body?.detail) return body.detail;
      return 'Resursa solicitată nu a fost găsită.';
    }

    // 0: no connection
    if (error.status === 0) {
      return 'Nu s-a putut contacta serverul. Verifică conexiunea la internet.';
    }

    // Other: try to extract detail from body
    if (body && typeof body === 'object' && body.detail) {
      return body.detail;
    }

    // Fallback: human-readable message without raw URL
    return `Eroare ${error.status || 'necunoscută'}. Încearcă din nou.`;
  }

  private formatValidationErrors(body: any): string {
    // DRF detail string
    if (typeof body.detail === 'string') {
      return body.detail;
    }

    // DRF non_field_errors
    if (Array.isArray(body.non_field_errors)) {
      return body.non_field_errors.join(' ');
    }

    // DRF field errors: { field_name: ["error1", "error2"] }
    const messages: string[] = [];
    for (const [field, errors] of Object.entries(body)) {
      if (Array.isArray(errors)) {
        const fieldLabel = field.replace(/_/g, ' ');
        messages.push(`${fieldLabel}: ${(errors as string[]).join(', ')}`);
      } else if (typeof errors === 'string') {
        const fieldLabel = field.replace(/_/g, ' ');
        messages.push(`${fieldLabel}: ${errors}`);
      }
    }

    if (messages.length > 0) {
      return messages.join('\n');
    }

    return 'Datele trimise sunt invalide. Verifică formularul.';
  }

  private getErrorSummary(status: number): string {
    switch (status) {
      case 400: return 'Date invalide';
      case 403: return 'Acces interzis';
      case 404: return 'Negăsit';
      case 500: return 'Eroare server';
      case 0: return 'Fără conexiune';
      default: return 'Eroare';
    }
  }
}
